import type { ProductLinkPreview, ProductMarketplaceLink } from "@nova-ai/product-detail-contract"
import { ChevronDown, ExternalLink, ImageOff } from "lucide-react"
import { useState } from "react"

export function ProductMarketplaceLinks({ link }: { link: ProductMarketplaceLink }) {
  if (link.kind === "unavailable") {
    return <p className="text-xs text-muted-foreground">Listing link unavailable</p>
  }
  if (link.kind === "product") {
    return <ProductBookmark url={link.url} preview={link.preview} />
  }
  return (
    <details className="group/listings">
      <summary className="flex w-fit cursor-pointer list-none items-center gap-2 rounded-full py-1 text-[13px] font-medium text-foreground outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <span>{link.variants.length.toLocaleString()} variant listings</span>

        <ChevronDown className="size-3.5 transition-transform group-open/listings:rotate-180 motion-reduce:transition-none" />
      </summary>

      <ul className="mt-3 grid gap-3 xl:grid-cols-2">
        {link.variants.map((variant) => (
          <li key={variant.simpleSku} className="min-w-0">
            <ProductBookmark
              url={variant.url}
              preview={variant.preview}
              simpleSku={variant.simpleSku}
            />
          </li>
        ))}
      </ul>
    </details>
  )
}

function ProductBookmark({
  url,
  preview,
  simpleSku,
}: {
  url: string
  preview: ProductLinkPreview
  simpleSku?: string
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group/bookmark flex h-full min-w-0 items-center gap-3 rounded-lg border border-border bg-card p-3 text-foreground outline-none transition-colors hover:border-foreground/25 focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ProductLinkImage url={preview.imageUrl} />

      <span className="min-w-0 flex-1 space-y-1">
        <span className="line-clamp-2 break-words text-sm font-medium">
          {preview.name ?? "Product listing"}
        </span>

        {preview.variantLabel ? (
          <span className="block break-words text-[13px]">{preview.variantLabel}</span>
        ) : null}

        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span className="break-all">{new URL(url).hostname}</span>

          {simpleSku ? <span className="break-all font-mono">SKU {simpleSku}</span> : null}
        </span>
      </span>

      <ExternalLink
        className="size-3.5 shrink-0 text-muted-foreground group-hover/bookmark:text-info"
        aria-hidden="true"
      />
    </a>
  )
}

function ProductLinkImage({ url }: { url: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  return (
    <span className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded bg-muted/35 p-1">
      {url && url !== failedUrl ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          className="size-full object-contain"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <>
          <ImageOff className="size-5 text-muted-foreground" aria-hidden="true" />

          <span className="sr-only">No product image</span>
        </>
      )}
    </span>
  )
}
